import React, { useCallback, useRef } from "react";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";

import { inRange } from "lodash";

import RadioButton from "../../buttons/RadioButton";

import { CardMedia } from "../../content/CardMedia";
import { Tooltip } from "../../content";
import { Theme } from "@mui/material";
import { useMediaQuery } from "@mui/material";

interface Props {
  additionalBodyContent?: React.ReactNode;
  additionalBodyContentDirection?: "column" | "row";
  additionalCardContent?: React.ReactNode;
  isBlock?: boolean;
  onClick: () => void;
  selected: boolean;
  title: string;
  direction: "row" | "column";
  hideTitle?: boolean;
  disabled?: boolean;
  helpText?: string;
  tooltip?: string;
  image?: string;
  subtitle?: string;
  variant?: "default" | "blank";
  fontSize?: number;
}

const FormRadioOption = ({
  additionalBodyContent,
  additionalBodyContentDirection = "row",
  additionalCardContent,
  isBlock,
  onClick,
  selected,
  title,
  direction,
  hideTitle,
  disabled,
  helpText,
  tooltip,
  image,
  subtitle,
  variant = "default",
  fontSize,
}: Props) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const iconButtonRef = useRef<HTMLButtonElement>(null);

  const clickedWithinIconBounds = useCallback(
    (e: React.MouseEvent<unknown, unknown>) => {
      // icon has not rendered
      if (!iconButtonRef.current) return false;

      // icon exists
      const { clientX, clientY } = e;
      const { x, y, width, height } =
        iconButtonRef.current.getBoundingClientRect();
      if (
        // we check for bounds, including edges, since
        // we want to include clicks on the edges of an icon
        inRange(clientX, x - 1, x + width + 1) &&
        inRange(clientY, y - 1, y + height + 1)
      )
        return true;
      // we're out of bounds
      return false;
    },
    []
  );

  const handleCardClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (disabled) {
      return;
    }
    if (clickedWithinIconBounds(e)) {
      return;
    }
    onClick();
  };

  return (
    <Card
      sx={(theme) => ({
        display: "flex",
        cursor: "pointer",
        borderWidth: 1,
        ...(variant === "blank"
          ? {
              border: "none",
            }
          : {
              "&:hover": {
                boxShadow: 2,
              },
              padding: theme.spacing(direction === "row" ? 0 : 3, 3),
              ...(selected && direction === "row" && !disabled
                ? {
                    backgroundColor: theme.palette.action.hover,
                  }
                : {}),
              ...(selected && direction === "column" && !disabled
                ? {
                    borderWidth: 2,
                    padding: theme.spacing(2.875),
                    borderStyle: `solid`,
                    borderColor: theme.palette.primary.main,
                  }
                : {}),
              ...(disabled
                ? {
                    opacity: 0.6,
                    "&, *": {
                      cursor: "not-allowed !important",
                    },
                    "&:hover": {
                      boxShadow: "none !important",
                    },
                  }
                : {}),
            }),
      })}
      aria-selected={selected}
      data-title={`${title}`}
      onClick={handleCardClick}
      style={isBlock ? { display: "block" } : {}}
      role="option"
      aria-labelledby={`${title}-label`}>
      <CardActions
        sx={(theme) => ({
          marginRight: { xs: theme.spacing(1), sm: theme.spacing(2) },
          padding: 0,
          height: theme.spacing(variant === "blank" ? 4 : 8),
          pointerEvents: "none",
        })}>
        <RadioButton
          data-title={`${title}-radio-button`}
          checked={selected}
          onClick={onClick}
          size={variant === "blank" ? "small" : "medium"}
          variant={variant}
        />
      </CardActions>
      <Box display="flex" flexDirection="column" flexGrow={1} minWidth={0}>
        <Box display="flex" flexGrow={1}>
          {image && <CardMedia image={image} />}
          <Box
            display="flex"
            flexGrow={1}
            minWidth={0}
            justifyContent="space-between"
            sx={{
              flexDirection: additionalBodyContent
                ? additionalBodyContentDirection
                : "row",
            }}>
            {!hideTitle && (
              <Box display="flex" alignItems="center">
                <CardHeader
                  title={title}
                  titleTypographyProps={{
                    "data-title": `${title}-title`,
                    id: `${title}-label`,
                    variant: variant === "blank" ? "body2" : "body1",
                    fontSize,
                  }}
                  subheaderTypographyProps={{
                    "data-title": `${title}-subtitle`,
                  }}
                  subheader={isDesktop ? subtitle : null}
                />
                <Typography
                  ml={1}
                  color={selected ? "secondary" : "textSecondary"}>
                  {helpText}
                </Typography>
              </Box>
            )}
            {additionalBodyContent}
            {tooltip && (
              <Tooltip
                buttonRef={iconButtonRef}
                dataTestId="form-radio-option-tooltip"
                tooltip={tooltip ?? ""}
                size="large"
                placement="bottom"
                sx={(theme) => ({
                  height: theme.spacing(3),
                  width: theme.spacing(3),
                  marginTop: theme.spacing(2.5),
                  marginLeft: "auto",
                  color: theme.palette.action.disabledBackground,
                })}
              />
            )}
          </Box>
        </Box>
        {!isDesktop && subtitle && (
          <Typography
            variant="body1"
            data-title={`${title}-subtitle`}
            component="span"
            sx={(theme) => ({
              color: theme.colors.grey1,
              fontSize: theme.typography.body2.fontSize,
            })}>
            {subtitle}
          </Typography>
        )}
      </Box>

      {additionalCardContent}
    </Card>
  );
};

export default FormRadioOption;
